// ABOUTME: Pure helpers turning a server LocalizedText into what the UI renders.
// ABOUTME: The server decides the language; the client only reacts to `state`.

import type { L10nFragment } from "@/graphql/main/gql/graphql"
import { LocalizationState } from "@/graphql/main/gql/graphql"

/** Badge to draw next to the text. */
export type L10nBadgeKind =
  /** Nothing to signal — the text is the author's own. */
  | "none"
  /** Machine translation is on screen; the original is one click away. */
  | "translated"
  /** A translation exists but the viewer's plan doesn't include it. */
  | "locked"
  /** A translation is being produced; the original is on screen meanwhile. */
  | "pending"

export interface L10nView {
  /** Text to render right now. */
  readonly display: string
  /** Author's text, byte-for-byte. Equals `display` unless a translation is shown. */
  readonly original: string
  readonly badge: L10nBadgeKind
  /** Whether a "show original" toggle makes sense (translation differs from the original). */
  readonly canShowOriginal: boolean
  /** Translation is being produced in the background — the screen should refetch shortly. */
  readonly isPending: boolean
}

/**
 * Resolves a `*L10n` field into render instructions.
 *
 * `value` is null whenever the paired plain field is null (the schema mirrors
 * nullability), and undefined on cached data written before WA-762 — both fall
 * back to `fallback`, which callers pass as the plain original field so a
 * missing translation never blanks the UI.
 */
export function resolveL10n(value: L10nFragment | null | undefined, fallback?: string | null): L10nView {
  if (!value) {
    const text = fallback ?? ""
    return { display: text, original: text, badge: "none", canShowOriginal: false, isPending: false }
  }

  const { text, original, state } = value

  switch (state) {
    case LocalizationState.Translated:
      return {
        display: text,
        original,
        badge: "translated",
        canShowOriginal: text !== original,
        isPending: false,
      }
    case LocalizationState.Paywalled:
      return { display: text, original, badge: "locked", canShowOriginal: false, isPending: false }
    case LocalizationState.Pending:
      // Show the original with a "translating" marker: without it the text just
      // looks untranslated, and the user has no idea more is coming.
      return { display: text, original, badge: "pending", canShowOriginal: false, isPending: true }
    // ORIGINAL / NOT_REQUESTED / FAILED — the author's text, nothing to signal.
    // FAILED stays silent on purpose: the backend re-attempts on its own after
    // ~24 h, so there is nothing for the user to act on. NOT_REQUESTED is
    // silent too because it lumps together "quota spent", "language not
    // detected", "text too long" and "feature off" — claiming any one of them
    // would be a guess. Once the backend exposes the reason, the quota case
    // gets its own marker here.
    case LocalizationState.Original:
    case LocalizationState.NotRequested:
    case LocalizationState.Failed:
      return { display: text, original, badge: "none", canShowOriginal: false, isPending: false }
  }
}

/**
 * Builds the `*L10n` twin for text the user just typed, for optimistic responses.
 *
 * The text is the author's own, so it is its own "translation": state ORIGINAL,
 * no badge. The next server response replaces it with the real resolution.
 */
export function optimisticL10n(text: string | null | undefined): L10nFragment {
  const value = text ?? ""
  return {
    __typename: "LocalizedText",
    text: value,
    original: value,
    originalLanguage: null,
    state: LocalizationState.Original,
    translationSource: null,
    // Only meaningful when state is NOT_REQUESTED; this text is the author's own.
    notRequestedReason: null,
  }
}

/** Same as {@link optimisticL10n}, but mirrors a nullable source field. */
export function optimisticL10nOrNull(text: string | null | undefined): L10nFragment | null {
  return text === null || text === undefined ? null : optimisticL10n(text)
}

/**
 * True when any `*L10n` field anywhere in a query result is still PENDING.
 *
 * Walks the result instead of taking a hand-collected list: translated fields
 * sit deep inside `calendarItems.records[].data`, and every screen would
 * otherwise have to thread them out by hand.
 */
export function hasPendingTranslation(data: unknown): boolean {
  if (Array.isArray(data)) {
    return data.some(hasPendingTranslation)
  }

  if (data === null || typeof data !== "object") {
    return false
  }

  const record = data as Record<string, unknown>
  if (record.__typename === "LocalizedText") {
    return record.state === LocalizationState.Pending
  }

  return Object.values(record).some(hasPendingTranslation)
}
